import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  Box,
  Chip,
  colors,
  Tab,
  Tabs,
  Tooltip,
  useTheme,
} from '@material-ui/core'

import useSnackbar from 'hooks/useSnackbar'
import useSourceOrigin from 'hooks/useSourceOrigin'
import helpers from 'helpers'
import * as service from 'service'

import { ActivityAlert, ActivityDetails, ActivityLogsTab, Comments } from '../'
import ActivityFormFields from '../ActivityFormFields'
import ActivityAttachments from '../ActivityAttachments'
import useStyles from './styles'

const ActivityFormContent = ({
  control,
  errors,
  isEdit,
  currentColumn,
  activity,
  columns,
  users,
  departments,
  onRemoveAttachment,
  initialComments,
  isMultiDepartment,
  isShow,
}) => {
  const [logs, setLogs] = useState([])
  const [commentsTab, setCommentsTab] = useState(0)
  const [logsLoading, setLogsLoading] = useState(false)

  const classes = useStyles()
  const theme = useTheme()
  const snackbar = useSnackbar()
  const { hasSourceOrigin, getSourceOriginPath } = useSourceOrigin()

  useEffect(() => {
    setLogs([])
    setCommentsTab(0)
  }, [activity?.id]) // eslint-disable-line

  useEffect(() => {
    if (commentsTab !== 1 || !activity?.id || logs.length > 0) return
    const loadLogs = async () => {
      setLogsLoading(true)
      try {
        const res = await service.dponet.kanbanTasks.getLogs({
          taskId: activity.id,
        })
        setLogs(res?.data?.dponetAudits || [])
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
      } finally {
        setLogsLoading(false)
      }
    }
    loadLogs()
  }, [commentsTab, activity?.id]) // eslint-disable-line

  return (
    <>
      <Box className={classes.leftPanel}>
        {isEdit && currentColumn && (
          <Box display="flex" alignItems="center" className={classes.headerRow}>
            <Tooltip
              title={currentColumn.title.length > 20 ? currentColumn.title : ''}
            >
              <Chip
                size="small"
                label={helpers.truncateUIText(currentColumn.title, 20)}
                className={classes.columnChip}
                style={{
                  backgroundColor: currentColumn.color,
                  color: currentColumn.textColor || '#FFFFFF',
                }}
              />
            </Tooltip>
            {activity?.targetCompanyId && (
              <Chip
                size="small"
                label="Encarregado (DPO)"
                className={classes.dpoChip}
              />
            )}
            {activity?.sourceableId && (
              <Chip
                size="small"
                label="Automática"
                className={classes.autoChip}
              />
            )}
          </Box>
        )}
        {activity?.targetCompanyId && (
          <ActivityAlert
            message="Essa atividade é do Encarregado (DPO), você não tem permissão de editá-la"
            color={theme.palette.info.dark}
          />
        )}
        {activity?.sourceableId && (
          <ActivityAlert
            message={
              activity?.sourceRemoved
                ? 'Essa atividade é automática, gerenciada pelo fluxo do sistema. A origem que gerou esta atividade foi removida.'
                : 'Essa atividade é automática, gerenciada pelo fluxo do sistema. Para ver a atividade original'
            }
            color={colors.pink[900]}
            {...(hasSourceOrigin(activity) && {
              linkText: 'Clique aqui',
              linkPath: getSourceOriginPath(activity),
            })}
          />
        )}
        <ActivityFormFields
          control={control}
          errors={errors}
          isCrossBoard={!!activity?.targetCompanyId}
          isShow={isShow}
        />
        <ActivityAttachments
          activity={activity}
          onRemoveAttachment={onRemoveAttachment}
          isShow={isShow}
        />
        {isEdit ? (
          <Box display="flex" flexDirection="column">
            <Tabs
              value={commentsTab}
              onChange={(_, v) => setCommentsTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Comentários" className={classes.tabLabel} />
              <Tab label="Histórico" className={classes.tabLabel} />
            </Tabs>
            <Box
              className={classes.tabPanel}
              style={{ display: commentsTab === 0 ? undefined : 'none' }}
            >
              <Comments
                taskId={activity?.id}
                initialComments={initialComments}
                initialCount={activity?.taskCommentsCount}
                disabled={!!activity?.unlinked}
              />
            </Box>
            <Box
              className={classes.tabPanel}
              style={{ display: commentsTab === 1 ? undefined : 'none' }}
            >
              <ActivityLogsTab logs={logs} logsLoading={logsLoading} />
            </Box>
          </Box>
        ) : (
          <Box>
            <Comments
              taskId={activity?.id}
              initialComments={initialComments}
              initialCount={activity?.taskCommentsCount}
              disabled={!!activity?.unlinked}
            />
          </Box>
        )}
      </Box>
      <Box className={classes.rightPanel}>
        <ActivityDetails
          columns={columns}
          users={users}
          departments={departments}
          activity={activity}
          currentColumn={currentColumn}
          isMultiDepartment={isMultiDepartment}
          isShow={isShow}
        />
      </Box>
    </>
  )
}

ActivityFormContent.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  isEdit: PropTypes.bool,
  currentColumn: PropTypes.object,
  activity: PropTypes.object,
  columns: PropTypes.array,
  users: PropTypes.array,
  departments: PropTypes.array,
  onRemoveAttachment: PropTypes.func,
  initialComments: PropTypes.array,
  isMultiDepartment: PropTypes.bool,
  isShow: PropTypes.bool,
}

export default ActivityFormContent
